import React, { useEffect, useMemo, useState } from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import  FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

const ReceiveAmountStep: React.FC<Props> = ({ onValidationChange }) => {
  const { t } = useTranslation();
  const receiverReceiveAmount = useSessionStore((s: SessionState) => s.receiverReceiveAmount);
  const referenceNumberSearchResult = useSessionStore((s: SessionState) => (s as any).referenceNumberSearchResult);
  const setReceiverReceiveAmount = useSessionStore((s: SessionState) => (s as any).setReceiverReceiveAmount);
  const validationError = useSessionStore((s: SessionState) => (s as any).receiveAmountValidationError);
  const setValidationError = useSessionStore((s: SessionState) => (s as any).setReceiveAmountValidationError);
  const [amount, setAmount] = useState<string>(receiverReceiveAmount || "");

  const parseAmount = (text: string): string => {
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  const isValid = useMemo(() => {
    const n = parseFloat(amount || "0");
    return !!amount && isFinite(n) && n > 0;
  }, [amount]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  const handleChange = (text: string) => {
    const next = parseAmount(text);
    setAmount(next);
    try { setReceiverReceiveAmount?.(next); } catch {}
    try { setValidationError?.(null); } catch {}
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("HOW_MUCH_ARE_YOU_RECEIVING")}</Text>
      <Text style={styles.subtitle}>{t("PLEASE_ENTER_AMOUNT")}</Text>
      <FloatingTextInputComponent
        testID="receiveAmount"
        label={t("RECEIVE_AMOUNT")}
        value={amount}
        onChangeText={handleChange}
      />
      {!!validationError && (
        <Text style={styles.error}>{validationError}</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
    paddingBottom: 200
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    paddingRight: 50,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 28
  },
  input: { flex: 1, fontSize: 16 },
  error: { color: "red", fontSize: 12, marginTop: 8 },
});

export default ReceiveAmountStep;


